/* eslint-disable i18next/no-literal-string */
import {SegmentedControl} from '@dropbox/dig-components/controls';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack, ThemeContainer, ThemeProvider} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ImageLine} from '@dropbox/dig-icons/assets';
import {derivedThemeAtom} from 'atoms/layout';
import {useAtomValue} from 'jotai';
import {useState} from 'react';
import {ManagedBanner, ManagedBannerContent} from 'views/home/ManagedBanner';

import styles from './HomeBanners.module.css';

type PreviewMode = 'bright' | 'dark';

// React 18's types don't know `inert` yet; the DOM does.
const INERT = {inert: ''} as Record<string, string>;

/**
 * How wide banners are on home with the default three-column dashboard
 * (`containerWidth3` in views/home/dashboard/Dashboard.module.css).
 */
export const HOME_BANNER_WIDTH = 1120;

const NoImage = () => (
  <Box display="flex" flexDirection="column" alignItems="center" className={styles.noImage}>
    <UIIcon src={ImageLine} className={atoms({color: 'Text Subtle'})} />
    <Text size="small" color="subtle">
      No image
    </Text>
  </Box>
);

/**
 * The banner as it will look on home, in light or dark mode, at home's width.
 * It uses the same renderer as home, on a page-colored stage for the theme.
 */
export const BannerPreview = ({
  banner,
  imageSrc,
  darkImageSrc,
}: {
  banner: ManagedBannerContent;
  imageSrc?: string;
  darkImageSrc?: string;
}) => {
  const appTheme = useAtomValue(derivedThemeAtom);
  const [mode, setMode] = useState<PreviewMode>(appTheme === 'dark' ? 'dark' : 'bright');

  return (
    <Stack gap="8">
      <Split alignY="center" gap="8">
        <Split.Item width="fill">
          <Text isBold>Preview</Text>
        </Split.Item>
        <Split.Item>
          <SegmentedControl
            aria-label="Preview theme"
            selectedValue={mode}
            onValueChange={(value) => value && setMode(value as PreviewMode)}
          >
            <SegmentedControl.Option variant="text" value="bright">
              Light
            </SegmentedControl.Option>
            <SegmentedControl.Option variant="text" value="dark">
              Dark
            </SegmentedControl.Option>
          </SegmentedControl>
        </Split.Item>
      </Split>
      <ThemeProvider mode={mode}>
        <ThemeContainer>
          <Box
            className={styles.previewStage}
            role="group"
            aria-label={`${mode === 'dark' ? 'Dark' : 'Light'} mode banner preview`}
          >
            {/* Inert: the button link and dismiss button only show where they sit. Clicking the
                link would leave the page and lose the form. */}
            <div {...INERT}>
              <ManagedBanner
                banner={banner}
                mode={mode}
                imageSrc={imageSrc}
                darkImageSrc={darkImageSrc}
                imagePlaceholder={<NoImage />}
                onDismiss={() => {}}
              />
            </div>
          </Box>
        </ThemeContainer>
      </ThemeProvider>
    </Stack>
  );
};
